import { useI18n } from '@/hooks/useI18n';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ExternalLink } from 'lucide-react';
import { Tile } from '@/components/primitives/Tile';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Skeleton } from '@/components/ui/skeleton';
import { useViralReels } from '@/hooks/queries/useViralReels';
import { formatDate, formatNumber, formatPeriodRange } from '@/lib/format';
import { buildReelUrl } from '@/lib/ig';
import { proxyImage } from '@/lib/imageProxy';
import type { OverviewSeriesPoint } from '@/types/api';
import { SectionHeader } from './SectionHeader';
import { AnalyticsEmpty } from './AnalyticsEmpty';

interface ViralTimelineSectionProps {
  periodDays: number;
  viralSeries: OverviewSeriesPoint[] | undefined;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
}

const ROSE = 'hsl(352 84% 60%)';

export function ViralTimelineSection({
  periodDays,
  viralSeries,
  isLoading,
  error,
  onRetry,
}: ViralTimelineSectionProps) {
  const { t } = useI18n();
  const viralReels = useViralReels(periodDays, 5);
  // Keep UTC daily bucket dates stable in every browser timezone.
  const data = (viralSeries ?? []).map((p) => ({
    t: p.date ?? p.timestamp.slice(0, 10),
    v: p.value,
  }));
  const hasData = data.some((d) => d.v > 0);

  return (
    <section className="min-w-0 space-y-3">
      <SectionHeader
        title={t('Reels devenus viraux', 'Reels that went viral')}
        subtitle={t(
          `Nouveaux reels détectés comme viraux ${formatPeriodRange(periodDays)}, par jour UTC.`,
          `New reels detected as viral ${formatPeriodRange(periodDays)}, by UTC day.`,
        )}
      />
      <Tile padded>
        {error ? (
          <ErrorInline error={error} onRetry={onRetry} />
        ) : isLoading ? (
          <Skeleton className="h-[180px] w-full rounded-[10px]" />
        ) : !viralSeries ? (
          <AnalyticsEmpty
            title={t('Historique indisponible', 'History unavailable')}
            description={t(
              'Les détections virales ne sont pas disponibles pour le moment.',
              'Viral detections are currently unavailable.',
            )}
          />
        ) : !hasData ? (
          <AnalyticsEmpty
            title={t('Aucun reel viral', 'No viral reels')}
            description={t(
              `Aucune détection virale enregistrée ${formatPeriodRange(periodDays)}.`,
              `No viral detections recorded ${formatPeriodRange(periodDays)}.`,
            )}
          />
        ) : (
          <div className="h-[180px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="hsl(30 14% 90%)" strokeDasharray="3 4" vertical={false} />
                <XAxis
                  dataKey="t"
                  tick={{ fill: 'hsl(240 5% 55%)', fontSize: 10 }}
                  stroke="transparent"
                  tickFormatter={(v) => {
                    try {
                      return formatDate(`${v}T12:00:00`, 'dd MMM');
                    } catch {
                      return '';
                    }
                  }}
                  minTickGap={40}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'hsl(240 5% 55%)', fontSize: 10 }}
                  stroke="transparent"
                  width={40}
                  tickFormatter={(v) => formatNumber(v)}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<ViralTooltip />}
                  cursor={{ fill: 'hsl(30 14% 90%)', opacity: 0.4 }}
                />
                <Bar dataKey="v" fill={ROSE} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Tile>

      {viralReels.isError ? (
        <ErrorInline error={viralReels.error} onRetry={() => void viralReels.refetch()} />
      ) : viralReels.isLoading && !viralReels.data ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="aspect-[9/16] rounded-[10px]" />
          ))}
        </div>
      ) : viralReels.data && viralReels.data.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[12px] text-text-3">
            {t(
              'Les 5 reels détectés le plus récemment · vues totales depuis leur publication.',
              'The 5 most recently detected reels · lifetime views since publication.',
            )}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {viralReels.data.slice(0, 5).map((reel) => (
              <a
                key={reel.reelId}
                href={buildReelUrl(reel.shortcode)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t(
                  `Ouvrir le reel ${reel.shortcode} sur Instagram`,
                  `Open reel ${reel.shortcode} on Instagram`,
                )}
                className="group relative aspect-[9/16] overflow-hidden rounded-[10px] border border-line-1 bg-bg-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
              >
                {reel.thumbnailUrl ? (
                  <img
                    src={proxyImage(reel.thumbnailUrl)}
                    alt={reel.caption ?? ''}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : null}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent p-2 text-white">
                  <span className="font-display text-[12px] tabular-nums">
                    {formatNumber(reel.maxViewsCount)}
                    <span className="mt-0.5 block text-[10px] font-normal">
                      {t('vues totales', 'lifetime views')}
                    </span>
                  </span>
                  <ExternalLink className="h-3 w-3 opacity-70" />
                </div>
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

interface TooltipPayloadItem {
  value: number | null;
}

function ViralTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
}) {
  useI18n();
  if (!active || !payload?.length) return null;
  const v = payload[0]?.value ?? 0;
  return (
    <div className="rounded-[10px] border border-line-1 bg-bg-1 px-3 py-2 shadow-card">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-text-3">
        {label ? formatDate(`${label}T12:00:00`, 'dd MMM yyyy') : '—'}
      </div>
      <div className="font-display mt-1 text-[14px] tabular-nums text-text-1">
        {formatNumber(Number(v))} reels
      </div>
    </div>
  );
}
